import {
  HomeIcon,
  BriefcaseIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  UserCircleIcon,
  FolderIcon,
  ChartBarIcon, // 👈 Ícone para OKR
  CalculatorIcon,
  BanknotesIcon,
  BuildingOffice2Icon,
  ShieldCheckIcon, // Ícone para o Portal do SI (Segurança da Informação, não só equipamento)
  UserPlusIcon, // Ícone para Recrutamento
} from "@heroicons/react/24/outline";
import {
  ROLE_ADMIN,
  ROLE_PROJECT_RADAR,
  ROLE_COLLABORATOR_ADMIN,
  ROLE_COLLABORATOR_VIEWER,
  ROLE_COLLABORATOR_EDITOR,
  ROLE_CALCULATOR,
} from "@/contexts/AuthContext";
import { ROLE_COMBINATIONS } from "@/router/proxyRouter.definitions";
import clsx from "clsx";
import type { NavigationItem } from "./layout.types";

// Base navigation structure
export const BASE_NAVIGATION: NavigationItem[] = [
  {
    name: "Dashboard",
    href: "/",
    icon: HomeIcon,
    requiredRoles: [
      ROLE_ADMIN,
      ROLE_PROJECT_RADAR,
      ROLE_COLLABORATOR_ADMIN,
      ROLE_COLLABORATOR_VIEWER,
    ],
  },
  // 👇 NOVO ITEM - Projetos
  {
    name: "Projetos",
    href: "/projects",
    icon: FolderIcon,
    requiredRoles: [
      ROLE_ADMIN,
      ROLE_PROJECT_RADAR,
      ROLE_COLLABORATOR_ADMIN,
      ROLE_COLLABORATOR_VIEWER,
    ],
  },
  {
    name: "Calculadora",
    href: "/calculator",
    icon: CalculatorIcon,
    requiredRoles: [
      ROLE_ADMIN,
      ROLE_PROJECT_RADAR,
      ROLE_COLLABORATOR_ADMIN,
      ROLE_COLLABORATOR_VIEWER,
      ROLE_CALCULATOR,
    ],
  },
  {
    name: "Gente & Gestão",
    href: "/people",
    icon: UserGroupIcon,
    requiredRoles: ROLE_COMBINATIONS.PEOPLE_ACCESS,
  },
  {
    name: "Propostas",
    href: "/proposals",
    icon: BriefcaseIcon,
    requiredRoles: [ROLE_ADMIN, ROLE_PROJECT_RADAR],
  },
  {
    name: "Colaboradores",
    href: "/collaborators",
    icon: UserCircleIcon,
    // Removido requiredRoles para permitir que todos os usuários vejam
  },
  {
    name: "Portal do DP",
    href: "/departamento-pessoal",
    icon: BuildingOffice2Icon,
  },
  {
    name: "Despesas",
    href: "/expenses",
    icon: BanknotesIcon,
    requiredRoles: [ROLE_ADMIN],
  },
  {
    name: "OKR",
    href: "/okr",
    icon: ChartBarIcon,
    requiredRoles: [
      ROLE_ADMIN,
      ROLE_PROJECT_RADAR,
      ROLE_COLLABORATOR_ADMIN,
      ROLE_COLLABORATOR_VIEWER,
    ],
  },
  {
    name: "Portal do SI",
    href: "/si",
    icon: ShieldCheckIcon,
    // Sem requiredRoles: qualquer usuário autenticado pode abrir/ver as próprias
    // solicitações (Discovery §10.4) — mesmo padrão já usado em "Colaboradores".
  },
  {
    name: "Recrutamento",
    href: "/recruitment",
    icon: UserPlusIcon,
    // Visível pra qualquer colaborador com alguma role no Digital — as ações
    // (adicionar candidato, links, editar, excluir) ficam restritas a
    // recruitment_admin dentro das próprias telas do módulo.
    requiredRoles: ROLE_COMBINATIONS.ANY_ROLE,
  },
  {
    name: "Administração",
    href: "/admin/users", // Default admin page link
    icon: Cog6ToothIcon,
    requiredRoles: [ROLE_ADMIN],
  },
];

// Utility function for conditional class names using clsx
export function classNames(...classes: string[]): string {
  return clsx(classes);
}

// Generate fallback avatar URL
export function generateFallbackAvatarUrl(name: string): string {
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(
    name
  )}&background=03AEFC&color=fff`;
}

// Check if current path is active - melhorada para sidebar
export function isNavigationItemActive(
  itemHref: string,
  currentPath: string
): boolean {
  if (itemHref === "/") {
    return currentPath === itemHref;
  }
  return currentPath.startsWith(itemHref);
}

export function getSidebarNavigationClasses(
  isActive: boolean,
  isMobile: boolean = false
): string {
  return clsx(
    "group flex gap-x-3 rounded-xl p-3 text-sm leading-6 font-montserrat font-medium transition-all duration-200",
    isActive
      ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-raro"
      : "text-raro-gray-700 hover:text-primary-600 hover:bg-primary-50 hover:shadow-sm",
    isMobile && "text-base p-4"
  );
}


export function getSidebarIconClasses(isActive: boolean): string {
  return clsx(
    "h-5 w-5 shrink-0 transition-colors",
    isActive ? "text-white" : "text-raro-gray-400 group-hover:text-primary-600"
  );
}

export function filterNavigationByRoles<T extends { requiredRoles?: string[] }>(
  navigation: T[],
  userHasRole: (role: string) => boolean
): T[] {
  return navigation.filter((item) => {
    if (!item.requiredRoles || item.requiredRoles.length === 0) {
      return true;
    }
    return item.requiredRoles.some((role) => userHasRole(role));
  });
}

export function shouldShowAdminMenu(
  pathname: string,
  userHasRole: (role: string) => boolean
): {
  canSeeAdminSection: boolean;
  isAdminSubPageContext: boolean;
  showAdminMenu: boolean;
} {
  const canSeeAdminSection = userHasRole(ROLE_ADMIN);
  const isAdminSubPageContext =
    pathname.startsWith("/admin/") && pathname !== "/admin/collaborators";
  const showAdminMenu = isAdminSubPageContext && canSeeAdminSection;

  return {
    canSeeAdminSection,
    isAdminSubPageContext,
    showAdminMenu,
  };
}
